import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { apiClient } from '../lib/api';
import { Role, Permission } from '../types';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { translatePermission, translateRole } from '../utils/translations';
import { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

interface RoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  role?: Role;
  onSave: (data: any) => Promise<void>;
}

const RoleModal: React.FC<RoleModalProps> = ({ isOpen, onClose, role, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permission_ids: [] as string[],
  });
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [allCategoriesAccess, setAllCategoriesAccess] = useState(false);

  const { admin } = useAuth();
  const canViewPermissions = hasPermission(admin, 'permissions', 'read');

  const { data: permissionsData, error: permissionsError } = useQuery(
    ['permissions'],
    () => apiClient.getPermissions({ limit: 100 }),
    { enabled: isOpen && canViewPermissions }
  );
  const { data: categoriesData } = useQuery(['categories', 'for-role-modal'], () => apiClient.getCategories({ limit: 100 }), { enabled: isOpen });

  useEffect(() => {
    if (role) {
      setFormData({
        name: role.name,
        description: role.description || '',
        permission_ids: role.permissions.map(p => p.id),
      });
      setCategoryIds(role.categories?.map(c => c.id) || []);
      setAllCategoriesAccess(role.all_categories_access || false);
    } else {
      setFormData({
        name: '',
        description: '',
        permission_ids: [],
      });
      setCategoryIds([]);
      setAllCategoriesAccess(false);
    }
  }, [role]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSave({ ...formData, category_ids: categoryIds, all_categories_access: allCategoriesAccess });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Ошибка сохранения роли');
    }
  };

  const handlePermissionToggle = (permissionId: string) => {
    setFormData(prev => ({
      ...prev,
      permission_ids: prev.permission_ids.includes(permissionId)
        ? prev.permission_ids.filter(id => id !== permissionId)
        : [...prev.permission_ids, permissionId],
    }));
  };

  const handleCategoryToggle = (categoryId: string) => {
    setCategoryIds(prev => prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]);
  };

  if (!isOpen) return null;

  const permissions = permissionsData?.data || [];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {role ? 'Редактировать роль' : 'Создать роль'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Название
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Описание
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            {canViewPermissions && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Права
                </label>
                <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {permissionsError && (permissionsError as AxiosError)?.response?.status === 403 ? (
                    <div className="text-sm text-red-600 py-2">Нет прав для просмотра прав</div>
                  ) : permissionsData?.data?.length === 0 ? (
                    <div className="text-sm text-gray-500 py-2">Нет доступных прав</div>
                  ) : !permissionsData?.data ? (
                    <div className="text-sm text-gray-500 py-2">Загрузка прав...</div>
                  ) : (
                    permissionsData.data.map((permission) => (
                      <label key={permission.id} className="flex items-center space-x-2 py-1">
                        <input
                          type="checkbox"
                          checked={formData.permission_ids.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">
                          {translatePermission(permission)}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Категории доступа</label>
              <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                {!categoriesData?.data ? (
                  <div className="text-sm text-gray-500 py-2">Загрузка категорий...</div>
                ) : categoriesData.data.length === 0 ? (
                  <div className="text-sm text-gray-500 py-2">Нет доступных категорий</div>
                ) : (
                  categoriesData.data.map((cat) => (
                    <label key={cat.id} className="flex items-center space-x-2 py-1">
                      <input
                        type="checkbox"
                        checked={categoryIds.includes(cat.id)}
                        onChange={() => handleCategoryToggle(cat.id)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        disabled={allCategoriesAccess}
                      />
                      <span className="text-sm text-gray-700">{cat.name}</span>
                    </label>
                  ))
                )}
              </div>
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="all_categories_access"
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  checked={allCategoriesAccess}
                  onChange={e => setAllCategoriesAccess(e.target.checked)}
                />
                <label htmlFor="all_categories_access" className="ml-2 block text-sm text-gray-900">
                  Доступ ко всем категориям
                </label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
              >
                Отмена
              </button>
              <button
                type="submit"
                className="btn btn-primary"
              >
                {role ? 'Обновить' : 'Создать'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleModal; 